import { BaseApiClient } from "../api/baseApiClient.service";
import { SubscriptionService } from "./subscription.service";
import { AnalyticsService } from "../analytics/analytics.service";
import { toast } from "sonner";
import i18next from "i18next";
import {
	closeCheckoutWindow,
	navigateCheckoutWindow,
} from "../stripe/checkoutWindow";

interface CheckoutResponse {
	success: boolean;
	sessionId?: string;
	url?: string;
	error?: string;
}

interface SubscriptionUpdateResponse {
	success: boolean;
	message: string;
	subscription?: any;
	error?: string;
}

type SubscriptionAction = "checkout" | "upgrade" | "downgrade";

/**
 * Service for purchasing and managing subscriptions via Stripe
 */
export class SubscriptionPurchaseService {
	private static readonly BASE_PATH = "/stripe";
	private static preOpenedCheckoutWindow: Window | null = null;

	static setPreOpenedCheckoutWindow(checkoutWindow: Window | null): void {
		SubscriptionPurchaseService.preOpenedCheckoutWindow = checkoutWindow;
	}

	/**
	 * Create a Stripe checkout session for new subscription
	 */
	static async createCheckoutSession(
		priceId: string,
	): Promise<CheckoutResponse> {
		const client = BaseApiClient.getInstance();
		const response = await client.post<CheckoutResponse, { priceId: string }>(
			`${SubscriptionPurchaseService.BASE_PATH}/create-checkout`,
			{ priceId },
		);

		return response;
	}

	/**
	 * Upgrade existing subscription to a higher tier
	 */
	static async upgradeSubscription(
		newPriceId: string,
		prorationBehavior:
			| "always_invoice"
			| "create_prorations"
			| "none" = "always_invoice",
	): Promise<SubscriptionUpdateResponse> {
		const client = BaseApiClient.getInstance();
		const response = await client.post<
			SubscriptionUpdateResponse,
			{ newPriceId: string; prorationBehavior?: string }
		>(`${SubscriptionPurchaseService.BASE_PATH}/upgrade-subscription`, {
			newPriceId,
			prorationBehavior,
		});

		return response;
	}

	/**
	 * Downgrade existing subscription to a lower tier
	 */
	static async downgradeSubscription(
		newPriceId: string,
		prorationBehavior: "none" | "create_prorations" = "none",
	): Promise<SubscriptionUpdateResponse> {
		const client = BaseApiClient.getInstance();
		const response = await client.post<
			SubscriptionUpdateResponse,
			{ newPriceId: string; prorationBehavior?: string }
		>(`${SubscriptionPurchaseService.BASE_PATH}/downgrade-subscription`, {
			newPriceId,
			prorationBehavior,
		});

		return response;
	}

	/**
	 * Determine what action to take based on current subscription status
	 * @param targetPriceId The price ID the user wants to subscribe to
	 * @param currentPlanId The user's current plan ID (from auth context)
	 * @param subscriptionStatus The user's current subscription status (from auth context)
	 * @returns The action to take (checkout, upgrade, or downgrade)
	 */
	private static async determineSubscriptionAction(
		targetPriceId: string,
		currentPlanId?: string,
		subscriptionStatus?: string,
	): Promise<{ action: SubscriptionAction }> {
		try {
			// No active subscription or failed states - use checkout
			const inactiveStatuses = [
				"NONE",
				"CANCELED",
				"UNPAID",
				"INCOMPLETE",
				"INCOMPLETE_EXPIRED",
			];
			if (
				!subscriptionStatus ||
				inactiveStatuses.includes(subscriptionStatus)
			) {
				return { action: "checkout" };
			}

			// Has active subscription - determine if upgrade or downgrade
			if (!currentPlanId) {
				return { action: "checkout" };
			}

			// Get current and target plan details to determine upgrade/downgrade
			const plans = await SubscriptionService.getPlans();
			const currentPlan = plans.find((p) => p.id === currentPlanId);
			const targetPlan = plans.find((p) => p.stripePriceId === targetPriceId);

			if (!currentPlan || !targetPlan) {
				// Can't determine, default to checkout
				return { action: "checkout" };
			}

			// Compare plan prices to determine upgrade/downgrade
			const currentPrice = currentPlan.price ?? 0;
			const targetPrice = targetPlan.price ?? 0;

			if (targetPrice > currentPrice) {
				return { action: "upgrade" };
			}
			if (targetPrice < currentPrice) {
				return { action: "downgrade" };
			}

			// Same price - still treat as upgrade to trigger immediate coin grant
			return { action: "upgrade" };
		} catch (error) {
			console.error("Failed to determine subscription action:", error);
			// Default to checkout if we can't determine
			return { action: "checkout" };
		}
	}

	/**
	 * Intelligently handle subscription purchase/change
	 * Automatically detects if user needs checkout, upgrade, or downgrade
	 * @param priceId The Stripe price ID to purchase
	 * @param currentPlanId Optional: User's current plan ID (from auth context)
	 * @param subscriptionStatus Optional: User's current subscription status (from auth context)
	 * @returns The Stripe checkout popup, so callers can detect a dismissed
	 * checkout. Null when no popup is involved (upgrade/downgrade, or a
	 * same-tab redirect because the popup was blocked).
	 */
	static async purchaseSubscription(
		priceId: string,
		currentPlanId?: string,
		subscriptionStatus?: string,
	): Promise<Window | null> {
		const preOpenedCheckoutWindow =
			SubscriptionPurchaseService.preOpenedCheckoutWindow;
		try {
			return await SubscriptionPurchaseService.completePurchase(
				priceId,
				currentPlanId,
				subscriptionStatus,
				preOpenedCheckoutWindow,
			);
		} catch (error) {
			// Still holding the blank tab — request failed before we handed it
			// to Stripe or closed it for an in-place upgrade/downgrade.
			if (
				SubscriptionPurchaseService.preOpenedCheckoutWindow ===
				preOpenedCheckoutWindow
			) {
				closeCheckoutWindow(preOpenedCheckoutWindow);
				SubscriptionPurchaseService.preOpenedCheckoutWindow = null;
			}
			throw error;
		}
	}

	private static async completePurchase(
		priceId: string,
		currentPlanId: string | undefined,
		subscriptionStatus: string | undefined,
		preOpenedCheckoutWindow: Window | null,
	): Promise<Window | null> {
		// Determine the appropriate action
		const { action } =
			await SubscriptionPurchaseService.determineSubscriptionAction(
				priceId,
				currentPlanId,
				subscriptionStatus,
			);

		// Track that the user initiated a checkout / plan change
		AnalyticsService.track("checkout_initiated", {
			price_id: priceId,
			action,
		});

		if (action === "upgrade") {
			closeCheckoutWindow(preOpenedCheckoutWindow);
			SubscriptionPurchaseService.preOpenedCheckoutWindow = null;
			// Direct API call for upgrade - no checkout needed
			const response =
				await SubscriptionPurchaseService.upgradeSubscription(priceId);
			if (response.success) {
				AnalyticsService.track("subscription_upgraded", {
					price_id: priceId,
				});
				// Show success toast
				toast.success(i18next.t("pricing.subscription.toasts.upgradeSuccess"));
				// Subscription updated successfully - reload page to reflect changes
				setTimeout(() => {
					window.location.reload();
				}, 1500);
			} else {
				throw new Error(response.error || "Failed to upgrade subscription");
			}
			return null;
		} else if (action === "downgrade") {
			closeCheckoutWindow(preOpenedCheckoutWindow);
			SubscriptionPurchaseService.preOpenedCheckoutWindow = null;
			// Direct API call for downgrade - no checkout needed
			const response =
				await SubscriptionPurchaseService.downgradeSubscription(priceId);
			if (response.success) {
				AnalyticsService.track("subscription_downgraded", {
					price_id: priceId,
				});
				// Show success toast
				toast.success(
					i18next.t("pricing.subscription.toasts.downgradeSuccess"),
				);
				// Subscription updated successfully - reload page to reflect changes
				setTimeout(() => {
					window.location.reload();
				}, 1500);
			} else {
				throw new Error(response.error || "Failed to downgrade subscription");
			}
			return null;
		} else {
			// New subscription - use checkout flow
			const response =
				await SubscriptionPurchaseService.createCheckoutSession(priceId);

			if (response.success && response.url) {
				SubscriptionPurchaseService.preOpenedCheckoutWindow = null;
				return navigateCheckoutWindow(preOpenedCheckoutWindow, response.url);
			} else {
				closeCheckoutWindow(preOpenedCheckoutWindow);
				SubscriptionPurchaseService.preOpenedCheckoutWindow = null;
				throw new Error(response.error || "Failed to create checkout session");
			}
		}
	}
}
